import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BranchProvider } from './context/BranchContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import SADashboard from './pages/superadmin/SADashboard';
import SAClients from './pages/superadmin/SAClients';
import SAVendors from './pages/superadmin/SAVendors';
import SASettings from './pages/superadmin/SASettings';
import VendorDashboard from './pages/vendor/VendorDashboard';
import VendorClients from './pages/vendor/VendorClients';
import VendorSettings from './pages/vendor/VendorSettings';
import AdminDashboard from './pages/admin/AdminDashboard';
import Reviews from './pages/admin/Reviews';
import ReviewDetail from './pages/admin/ReviewDetail';
import Social from './pages/admin/Social';
import CreatePost from './pages/admin/CreatePost';
import Departments from './pages/admin/Departments';
import Platforms from './pages/admin/Platforms';
import Reports from './pages/admin/Reports';
import Settings from './pages/admin/Settings';
import Branches from './pages/admin/Branches';
import GoogleMyBusiness from './pages/admin/GoogleMyBusiness';
import ShareReviewLink from './pages/admin/ShareReviewLink';
import EventQR from './pages/admin/EventQR';
import OAuthCallback from './pages/admin/OAuthCallback';
import ReviewForm from './pages/public/ReviewForm';
import EventRegistration from './pages/public/EventRegistration';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import TermsAndConditions from './pages/public/TermsAndConditions';
import BlogPublicList from './pages/public/BlogPublicList';
import BlogDetail from './pages/public/BlogDetail';
import WebsitePublic from './pages/public/WebsitePublic';
import HomePage from './pages/public/HomePage';
import Blogs from './pages/admin/Blogs';
import DeptDashboard from './pages/dept/DeptDashboard';
import DeptSettings from './pages/dept/DeptSettings';
import './App.css';

function LoadingScreen() {
  // Best-effort: read cached user from localStorage so the loader matches the
  // vendor brand even before AuthContext finishes hydrating. Falls back to
  // "CB" (CredBloom) when no cached vendor brand exists.
  let initial = 'CB';
  let bg;
  try {
    const cached = JSON.parse(localStorage.getItem('handleey_user') || 'null');
    const brand = cached?.vendor?.app_name;
    if (brand && brand.trim()) initial = brand.trim()[0].toUpperCase();
    if (cached?.vendor?.primary_color) bg = cached.vendor.primary_color;
  } catch { /* ignore corrupt cache */ }
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary"
          style={bg ? { backgroundColor: bg } : undefined}
          data-testid="loading-brand-tile"
        >
          <span className="text-primary-foreground font-bold text-sm">{initial}</span>
        </div>
        <div
          className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin border-primary"
          style={bg ? { borderColor: bg, borderTopColor: 'transparent' } : undefined}
        />
      </div>
    </div>
  );
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user?.role)) return <Navigate to="/" replace />;
  return children;
}

function RoleRedirect() {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role === 'super_admin') return <Navigate to="/super-admin" replace />;
  if (user?.role === 'vendor') return <Navigate to="/vendor-admin" replace />;
  if (user?.role === 'business_admin') return <Navigate to="/admin" replace />;
  if (user?.role === 'department') return <Navigate to="/dept" replace />;
  return <Navigate to="/login" replace />;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AuthProvider>
        <BranchProvider>
          <Toaster position="top-right" richColors closeButton />
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/vendor/:slug/login" element={<Login />} />
              <Route path="/review/:branchId" element={<ReviewForm />} />
              <Route path="/event/:eventId" element={<EventRegistration />} />
              <Route path="/pp" element={<PrivacyPolicy />} />
              <Route path="/tandc" element={<TermsAndConditions />} />
              <Route path="/blog/:slug" element={<BlogPublicList />} />
              <Route path="/blog/:slug/:blogSlug" element={<BlogDetail />} />
              <Route path="/site/:subdomain" element={<WebsitePublic />} />
              <Route path="/" element={<HomePage />} />

              {/* Super Admin */}
              <Route element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route path="/super-admin" element={<SADashboard />} />
                <Route path="/super-admin/clients" element={<SAClients />} />
                <Route path="/super-admin/vendors" element={<SAVendors />} />
                <Route path="/super-admin/share-review" element={<ShareReviewLink />} />
                <Route path="/super-admin/settings" element={<SASettings />} />
              </Route>

              {/* Vendor (reseller) */}
              <Route element={
                <ProtectedRoute allowedRoles={['vendor']}>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route path="/vendor-admin" element={<VendorDashboard />} />
                <Route path="/vendor-admin/clients" element={<VendorClients />} />
                <Route path="/vendor-admin/settings" element={<VendorSettings />} />
              </Route>

              {/* Business Admin */}
              <Route element={
                <ProtectedRoute allowedRoles={['business_admin']}>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/reviews" element={<Reviews />} />
                <Route path="/admin/reviews/:id" element={<ReviewDetail />} />
                <Route path="/admin/social" element={<Social />} />
                <Route path="/admin/create-post" element={<CreatePost />} />
                <Route path="/admin/departments" element={<Departments />} />
                <Route path="/admin/platforms" element={<Platforms />} />
                <Route path="/admin/reports" element={<Reports />} />
                <Route path="/admin/settings" element={<Settings />} />
                <Route path="/admin/branches" element={<Branches />} />
                <Route path="/admin/gmb" element={<GoogleMyBusiness />} />
                <Route path="/admin/share-review" element={<ShareReviewLink />} />
                <Route path="/admin/events" element={<EventQR />} />
                <Route path="/admin/blogs" element={<Blogs />} />
                <Route path="/admin/oauth/callback" element={<OAuthCallback />} />
              </Route>

              {/* Department User */}
              <Route element={
                <ProtectedRoute allowedRoles={['department']}>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route path="/dept" element={<DeptDashboard />} />
                <Route path="/dept/share-review" element={<ShareReviewLink />} />
                <Route path="/dept/settings" element={<DeptSettings />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </BranchProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
